import axios from "axios";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/refresh`,
            { refreshToken },
          );

          const { token, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem("token", token);
          localStorage.setItem("refreshToken", newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Auth API endpoints
export const authAPI = {
  // Register new user
  register: async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Registration failed" };
    }
  },

  // Verify email OTP
  verifyOTP: async (email, otp) => {
    try {
      const response = await api.post("/auth/verify-otp", { email, otp });
      if (response.data.success) {
        // Store tokens and user data
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "OTP verification failed" };
    }
  },

  // Resend OTP
  resendOTP: async (email) => {
    try {
      const response = await api.post("/auth/resend-otp", { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to resend OTP" };
    }
  },

  // Login
  login: async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      if (response.data.success) {
        // Store tokens and user data
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Login failed" };
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to send reset email" };
    }
  },

  // Verify reset OTP
  verifyResetOTP: async (email, otp) => {
    try {
      const response = await api.post("/auth/verify-reset-otp", { email, otp });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "OTP verification failed" };
    }
  },

  // Reset password
  resetPassword: async (resetToken, newPassword, confirmPassword) => {
    try {
      const response = await api.post("/auth/reset-password", {
        resetToken,
        newPassword,
        confirmPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Password reset failed" };
    }
  },

  // Logout
  logout: async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      await api.post("/auth/logout", { refreshToken });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    }
  },

  // Get current user
  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem("token");
  },
};

export const systemAPI = {
  getSystemStatus: async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const response = await axios.get(`${baseURL}/health/system-status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch system status" };
    }
  },
};

// User API endpoints
export const userAPI = {
  // Get current user profile
  getProfile: async () => {
    try {
      const response = await api.get("/users/me");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get profile" };
    }
  },

  // Get user basic details by user id
  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get user details" };
    }
  },

  // Update user profile
  updateProfile: async (data) => {
    try {
      const response = await api.put("/users/me", data);
      // Update local storage
      if (response.data.user) {
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({ ...currentUser, ...response.data.user }),
        );
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to update profile" };
    }
  },

  // Update nominee information
  updateNominee: async (data) => {
    try {
      const response = await api.put("/users/nominee", data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to update nominee info" };
    }
  },

  // Update withdrawal information
  updateWithdrawal: async (data) => {
    try {
      const response = await api.put("/users/withdrawal", data);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to update withdrawal info" }
      );
    }
  },

  // Toggle 2FA
  toggle2FA: async (enabled) => {
    try {
      const response = await api.post("/users/2fa/toggle", { enabled });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to toggle 2FA" };
    }
  },

  // Get compensation income summary and ledger
  getIncomeSummary: async () => {
    try {
      const response = await api.get("/users/me/income-summary");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch income summary" };
    }
  },

  getDashboardMetrics: async () => {
    try {
      const response = await api.get("/users/me/dashboard-metrics");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to fetch dashboard metrics" }
      );
    }
  },

  // Change password
  changePassword: async (oldPassword, newPassword) => {
    try {
      const response = await api.post("/users/change-password", {
        oldPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to change password" };
    }
  },
};

// Referral / team API endpoints (paths use /mlm/...)
export const mlmAPI = {
  // Get user's referral code
  getMyReferralCode: async () => {
    try {
      const response = await api.get("/mlm/my-referral-code");
      return response.data.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get referral code" };
    }
  },

  // Validate referral code
  validateCode: async (code) => {
    try {
      const response = await api.get(`/mlm/validate-code/${code}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Invalid referral code" };
    }
  },

  // Get direct referrals
  getDirectReferrals: async () => {
    try {
      const response = await api.get("/mlm/referrals/direct");
      return response.data.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get direct referrals" };
    }
  },

  getDirectLegBusiness: async () => {
    try {
      const response = await api.get("/mlm/referrals/direct-legs/business");
      return response.data.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to load direct leg business" }
      );
    }
  },

  // Get level-wise referrals
  getLevelWiseReferrals: async (maxLevel = 20) => {
    try {
      const response = await api.get(
        `/mlm/referrals/level-wise?maxLevel=${maxLevel}`,
      );
      return response.data.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to get level-wise referrals" }
      );
    }
  },

  // Get hierarchical tree
  getHierarchicalTree: async () => {
    try {
      const response = await api.get("/mlm/tree/hierarchical");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get tree" };
    }
  },

  // Get referral statistics
  getStats: async () => {
    try {
      const response = await api.get("/mlm/stats");
      return response.data.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get stats" };
    }
  },

  getTeamAggregates: async () => {
    try {
      const response = await api.get("/mlm/team/aggregates");
      return response.data.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to load team aggregates" };
    }
  },

  getDownlineMemberSummary: async (memberId) => {
    try {
      const response = await api.get(`/mlm/downline/${memberId}/summary`);
      return response.data.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to load member summary" }
      );
    }
  },

  getCompensationPublicSummary: async () => {
    try {
      const response = await api.get("/mlm/compensation/public-summary");
      return response.data.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to load compensation" };
    }
  },

  // Get earnings history
  getEarnings: async (page = 1, limit = 20) => {
    try {
      const response = await api.get(
        `/mlm/earnings?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get earnings" };
    }
  },

  // Get commission history
  getCommissions: async (page = 1, limit = 20) => {
    try {
      const response = await api.get(
        `/mlm/commissions?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get commissions" };
    }
  },
};

// Wallet API endpoints
export const walletAPI = {
  // Get wallet details with statistics
  getWallet: async () => {
    try {
      const response = await api.get("/wallet");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get wallet" };
    }
  },

  /** Phased split: mainEligible for arbitrage sizing until dual-wallet DB fields exist */
  getTradingCap: async () => {
    try {
      const response = await api.get("/wallet/trading-cap");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get trading cap" };
    }
  },

  // Get bot license status
  getBotLicenseStatus: async () => {
    try {
      const response = await api.get("/wallet/bot-license/status");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to fetch bot license status" }
      );
    }
  },

  // Purchase bot license fee
  purchaseBotLicense: async () => {
    try {
      const response = await api.post("/wallet/bot-license/purchase");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to purchase bot license" };
    }
  },

  // Get wallet transactions
  getTransactions: async (queryString = "") => {
    try {
      const url = queryString
        ? `/wallet/transactions?${queryString}`
        : "/wallet/transactions";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to get transactions" };
    }
  },

  // Create deposit request
  deposit: async (amount, network) => {
    try {
      const response = await api.post("/wallet/deposit", { amount, network });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Deposit request failed" };
    }
  },

  // Create withdrawal request with OTP
  requestWithdrawalOTP: async (
    amount,
    network,
    address,
    twoFAToken = null,
    withdrawalKind = "profit",
  ) => {
    try {
      const payload = {
        amount,
        network,
        address,
        withdrawalKind,
      };

      if (twoFAToken) {
        payload.twoFAToken = twoFAToken;
      }

      const response = await api.post("/wallet/withdraw/request-otp", payload);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to request withdrawal OTP" }
      );
    }
  },

  // Verify withdrawal OTP
  verifyWithdrawalOTP: async (otp, twoFAToken = null) => {
    try {
      const payload = { otp };

      // Add 2FA token if provided
      if (twoFAToken) {
        payload.twoFAToken = twoFAToken;
      }

      const response = await api.post("/wallet/withdraw/verify-otp", payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "OTP verification failed" };
    }
  },

  // Old withdraw method (deprecated)
  withdraw: async (amount, network, address) => {
    try {
      const response = await api.post("/wallet/withdraw", {
        amount,
        network,
        address,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Withdrawal request failed" };
    }
  },

  // Get withdrawal history
  getWithdrawals: async (params = {}) => {
    try {
      const response = await api.get("/wallet/withdrawals", { params });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to fetch withdrawal history" }
      );
    }
  },

  // Credit wallet (for testing)
  credit: async (amount) => {
    try {
      const response = await api.post("/wallet/credit", { amount });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Credit failed" };
    }
  },

  // Debit wallet (for testing)
  debit: async (amount) => {
    try {
      const response = await api.post("/wallet/debit", { amount });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Debit failed" };
    }
  },
};

// Admin Withdrawal API endpoints
export const adminWithdrawalAPI = {
  // Get pending withdrawals
  getPendingWithdrawals: async (page = 1, limit = 20) => {
    try {
      const response = await api.get(
        `/admin/withdrawals/pending?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { error: "Failed to fetch pending withdrawals" }
      );
    }
  },

  // Get all withdrawals
  getAllWithdrawals: async (page = 1, limit = 20, status = null) => {
    try {
      const url = status
        ? `/admin/withdrawals/all?page=${page}&limit=${limit}&status=${status}`
        : `/admin/withdrawals/all?page=${page}&limit=${limit}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch withdrawals" };
    }
  },

  // Get withdrawal statistics
  getStatistics: async () => {
    try {
      const response = await api.get("/admin/withdrawals/statistics");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch statistics" };
    }
  },

  // Approve withdrawal
  approveWithdrawal: async (id) => {
    try {
      const response = await api.post(`/admin/withdrawals/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to approve withdrawal" };
    }
  },

  // Reject withdrawal
  rejectWithdrawal: async (id, reason) => {
    try {
      const response = await api.post(`/admin/withdrawals/${id}/reject`, {
        reason,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to reject withdrawal" };
    }
  },
};

// Gold Trading API
export const goldAPI = {
  // Get current gold prices
  getCurrentPrices: async () => {
    try {
      const response = await api.get("/gold/prices");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch gold prices" };
    }
  },

  // Buy gold (send ounces and pricePerOz)
  buyGold: async ({ ounces, pricePerOz }) => {
    try {
      const response = await api.post("/gold/buy", { ounces, pricePerOz });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to buy gold" };
    }
  },

  // Sell gold (send ounces and pricePerOz)
  sellGold: async ({ ounces, pricePerOz }) => {
    try {
      const response = await api.post("/gold/sell", { ounces, pricePerOz });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to sell gold" };
    }
  },

  // Get user's gold holdings
  getHoldings: async () => {
    try {
      const response = await api.get("/gold/holdings");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch holdings" };
    }
  },

  // Get order history
  getOrders: async (params = {}) => {
    try {
      const response = await api.get("/gold/orders", { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch orders" };
    }
  },

  // Get P&L summary
  getPnL: async () => {
    try {
      const response = await api.get("/gold/pnl");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch P&L" };
    }
  },

  // Get portfolio summary
  getPortfolio: async () => {
    try {
      const response = await api.get("/gold/portfolio");
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: "Failed to fetch portfolio" };
    }
  },
};

export default api;
